import {
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Lock,
  Star,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { ApiError, mensajeDe } from '@/api/errors';
import { useEntitlements } from '@/auth/queries';
import { BotonBiColor } from '@/components/BloquesConsalud';
import { LogoCaso } from '@/components/MarcaCaso';
import { AVATARES } from '@/lib/avatares';
import { answerFeedback, animateCSS, pressFeedback, usePageEnter } from '@/lib/motion';
import { cn } from '@/lib/utils';

import {
  Cargando,
  ErrorCarga,
  Reproductor,
  TabsModulo,
  TextoMarcado,
  VisorLaminas,
} from './componentes';
import {
  useAccionClase,
  useClase,
  usePestana,
  type ClassCase,
  type ClassDetail,
  type ClassQuiz,
  type Guia,
  type Lamina,
  type Letra,
  type PresentacionDeClase,
  type RespuestaCaso,
  type ResumenDeClase,
  type TabKey,
  type Tipo,
  type Video,
} from './queries';

/** Pestaña ↔ segmento de URL (en español, como el resto de rutas). */
const SLUG: Record<TabKey, string> = {
  case: 'caso',
  videos: 'master-class',
  ultra_summaries: 'ultra-resumen',
  gpc_summaries: 'resumenes-gpc',
  medical_english: 'ingles-medico',
  gpc_guides: 'guias-gpc',
  enarm_plus: 'enarm-plus',
  presentation: 'presentacion',
  quiz: 'quiz',
  answer: 'respuesta',
  forum: 'foro',
  bibliography: 'bibliografia',
};
const DE_SLUG = Object.fromEntries(Object.entries(SLUG).map(([k, v]) => [v, k])) as Record<
  string,
  TabKey
>;

export function ClasePage({ tipo }: { tipo: Tipo }) {
  const { id: idParam, pestana } = useParams();
  const id = Number(idParam);
  const { data, error, isPending } = useClase(tipo, id);
  const acciones = useAccionClase(tipo, id);
  const { data: ent } = useEntitlements();
  const root = useRef<HTMLDivElement>(null);
  const vistaContada = useRef(false);
  const base = tipo === 'class' ? '/curso-enarm/clases' : '/curso-enarm/talleres';
  usePageEnter(root, [id]);

  // Una reproducción por apertura (como el PHP); el ref evita el doble efecto de StrictMode.
  useEffect(() => {
    if (!data || vistaContada.current) return;
    vistaContada.current = true;
    if (data.progress.caseAnswered) acciones.sumarVista.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!Number.isInteger(id) || id <= 0) return <Navigate to={base} replace />;
  if (isPending) return <Cargando alto="h-96" />;
  if (error) {
    const limite = error instanceof ApiError && error.code === 'CLASS_LIMIT_REACHED';
    return (
      <ErrorCarga error={error}>
        {limite && (
          <p className="mt-2 text-sm text-texto-2">
            Si necesitas volver a verla, escríbenos y lo revisamos contigo.
          </p>
        )}
        <Link to={base} className="btn-cs btn-contorno mt-4">
          Volver a la lista
        </Link>
      </ErrorCarga>
    );
  }

  const activa: TabKey =
    pestana && DE_SLUG[pestana] && data.tabs.some((t) => t.key === DE_SLUG[pestana])
      ? DE_SLUG[pestana]
      : 'case';
  const tab = data.tabs.find((t) => t.key === activa);
  const etiqueta = tipo === 'class' ? 'Clase' : 'Taller';

  return (
    <div ref={root} className="space-y-5">
      <TabsModulo />
      {/* Cabecero del legacy: nombre en marino + línea degradada, rótulo de especialidad y botones. */}
      <header className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-start">
        <div className="min-w-0">
          <h1 className="rounded-[10px] bg-marino-800 px-4 py-2 text-base font-semibold text-white uppercase sm:text-lg">
            <strong className="font-extrabold">
              {etiqueta} {data.number ?? ''}
            </strong>
            : {data.name}
          </h1>
          <div aria-hidden className="my-2 h-1 rounded-full bg-marca" />
          <p className="font-extrabold text-azul-texto uppercase">
            Curso ENARM {ent?.enarmYear ?? ''}
          </p>
          <p className="text-xs text-texto-3">
            Consultada {data.progress.views} {data.progress.views === 1 ? 'vez' : 'veces'}
            {data.progress.limit !== null &&
              ` · Limitada a ${String(data.progress.limit)} reproducciones`}
          </p>
        </div>
        <div className="self-start rounded-[10px] bg-marca px-4 py-1.5 text-center text-xs text-white uppercase">
          <b>{data.specialty}</b>
          <span className="block">
            {etiqueta} {data.number ?? '–'} de {data.total}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 md:col-span-2 lg:col-span-1 lg:justify-end">
          {data.prev && (
            <Link to={`${base}/${String(data.prev.id)}`} className="btn-cs btn-primario">
              <ChevronLeft size={16} /> {etiqueta} anterior
            </Link>
          )}
          <Link to={base} className="btn-cs btn-peligro">
            <X size={16} /> Salir de {etiqueta.toLowerCase()}
          </Link>
          {data.next && (
            <Link to={`${base}/${String(data.next.id)}`} className="btn-cs btn-exito">
              Siguiente {etiqueta.toLowerCase()} <ChevronRight size={16} />
            </Link>
          )}
        </div>
      </header>

      <nav
        aria-label="Secciones de la clase"
        className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:justify-center sm:px-0"
      >
        {data.tabs.map((t) => {
          const bloqueada = t.requiresCase;
          return (
            <Link
              key={t.key}
              to={`${base}/${String(id)}/${SLUG[t.key]}`}
              aria-current={t.key === activa ? 'page' : undefined}
              aria-disabled={bloqueada || undefined}
              onClick={(e) => {
                if (bloqueada) {
                  e.preventDefault();
                  toast.info('Para poder ver la clase resuelve el caso clínico.', {
                    id: 'requiere-caso',
                  });
                }
              }}
              className={cn(
                // Pestañas del legacy: grises y la activa con el degradado de marca.
                'inline-flex shrink-0 items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-bold no-underline transition-colors',
                t.key === activa
                  ? 'bg-marca text-white hover:text-white'
                  : 'vidrio text-texto-2 hover:text-texto',
                bloqueada && 'opacity-50',
              )}
            >
              {bloqueada && <Lock size={13} aria-label="Bloqueada" />}
              {t.label}
              {t.locked && <span className="insignia insignia-exclusivo">Premium</span>}
            </Link>
          );
        })}
      </nav>

      <section aria-live="polite">
        {tab?.locked ? (
          <Premium />
        ) : (
          <Panel tipo={tipo} clase={data} pestana={activa} base={base} acciones={acciones} />
        )}
      </section>
    </div>
  );
}

function Premium() {
  return (
    <div className="panel-vidrio mx-auto max-w-xl text-center">
      <Lock className="mx-auto text-cian-texto" size={36} />
      <p className="mt-3 font-bold">Tu licencia actual no incluye esta sección.</p>
      <Link to="/licencias" className="btn-cs btn-primario mt-4">
        Ver licencias
      </Link>
    </div>
  );
}

function Panel({
  tipo,
  clase,
  pestana,
  base,
  acciones,
}: {
  tipo: Tipo;
  clase: ClassDetail;
  pestana: TabKey;
  base: string;
  acciones: ReturnType<typeof useAccionClase>;
}) {
  const id = clase.id;
  switch (pestana) {
    case 'case':
      return <CasoClinico tipo={tipo} clase={clase} base={base} acciones={acciones} />;
    case 'videos':
      return <MasterClass tipo={tipo} id={id} />;
    case 'ultra_summaries':
      return <Laminas tipo={tipo} id={id} sub="/ultra-summaries" titulo="Ultra resumen" />;
    case 'enarm_plus':
      return <Laminas tipo={tipo} id={id} sub="/flashcards?kind=enarm_plus" titulo="ENARM Plus" />;
    case 'medical_english':
      return (
        <Laminas
          tipo={tipo}
          id={id}
          sub="/flashcards?kind=medical_english"
          titulo="Inglés médico"
        />
      );
    case 'gpc_guides':
      return <Guias tipo={tipo} id={id} />;
    case 'gpc_summaries':
      return <ResumenesGpc tipo={tipo} id={id} />;
    case 'presentation':
      return <Presentacion tipo={tipo} id={id} acciones={acciones} />;
    case 'quiz':
      return <Quiz tipo={tipo} id={id} acciones={acciones} />;
    case 'answer':
      return <Respuesta tipo={tipo} clase={clase} acciones={acciones} />;
    case 'forum':
      return (
        <div className="panel-vidrio mx-auto max-w-xl text-center">
          <p className="text-lg">
            ¡Únete a nuestro grupo de <b>Telegram</b>
            <br />
            para resolver tus dudas de clase!
          </p>
          {/* Doctores señalando el botón bicolor, como `pestanias/foro.php`. */}
          <img
            src="/img/curso/doctores-foro.png"
            alt=""
            className="pointer-events-none mx-auto mt-4 -mb-1 w-56 max-w-full"
          />
          <BotonBiColor icono={<Users size={26} />} href={clase.telegramUrl}>
            Unirme al grupo
          </BotonBiColor>
        </div>
      );
    case 'bibliography':
      return (
        <article className="superficie-lectura p-6">
          <h2 className="flex items-center gap-2 font-extrabold text-azul-texto">
            <BookOpen size={18} /> Bibliografía
          </h2>
          <p className="mt-3 leading-relaxed whitespace-pre-line">{clase.bibliography}</p>
        </article>
      );
  }
}

/** Opciones A–D (skill §6): caja de letra verde + campo; la marcada se pinta amarilla. */
function Opciones({
  opciones,
  seleccion,
  onSeleccion,
  estado,
  deshabilitadas,
}: {
  opciones: { key: Letra; text: string }[];
  seleccion: Letra | null;
  onSeleccion?: (l: Letra) => void;
  estado?: (l: Letra) => 'correcta' | 'incorrecta' | undefined;
  deshabilitadas?: boolean;
}) {
  return (
    <div role="radiogroup" className="space-y-2.5">
      {opciones.map((o) => {
        const e = estado?.(o.key) ?? (seleccion === o.key ? 'seleccionada' : undefined);
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={seleccion === o.key}
            disabled={deshabilitadas}
            data-opcion={o.key}
            data-estado={e}
            className="opcion w-full text-left disabled:cursor-default"
            onClick={(ev) => {
              pressFeedback(ev.currentTarget);
              onSeleccion?.(o.key);
            }}
          >
            <span className="opcion__letra flex flex-col gap-0.5">
              {o.key}
              <LogoCaso variante="blanco" className="h-2" />
            </span>
            <span className="opcion__texto">{o.text}</span>
          </button>
        );
      })}
    </div>
  );
}

function CasoClinico({
  tipo,
  clase,
  base,
  acciones,
}: {
  tipo: Tipo;
  clase: ClassDetail;
  base: string;
  acciones: ReturnType<typeof useAccionClase>;
}) {
  const navigate = useNavigate();
  const { data, error, isPending } = usePestana<ClassCase>(tipo, clase.id, '/case');
  const [elegida, setSeleccion] = useState<Letra | null>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const seleccion = elegida ?? data?.selected ?? null;

  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  const siguiente = clase.tabs.find((t) => t.key !== 'case' && !t.locked);

  return (
    <div className="panel-vidrio space-y-5">
      {!clase.progress.caseAnswered && (
        <p className="pill-marca mx-auto block w-fit text-center font-extrabold uppercase">
          Para poder ver la clase resuelve el caso clínico
        </p>
      )}
      {data.case && (
        <article className="superficie-lectura p-5 leading-relaxed">
          <LogoCaso className="mr-2 align-[-2px]" />
          <b>{data.case}</b>
        </article>
      )}
      {data.question && (
        <p className="barra-pregunta">
          <LogoCaso variante="blanco" className="shrink-0" />
          {data.question}
        </p>
      )}
      <Opciones opciones={data.options} seleccion={seleccion} onSeleccion={setSeleccion} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-texto-3">{data.reference}</span>
        <button
          ref={boton}
          type="button"
          className="btn-cs btn-exito"
          disabled={!seleccion || acciones.responderCaso.isPending}
          onClick={() => {
            if (!seleccion) return;
            acciones.responderCaso.mutate(seleccion, {
              onSuccess: (r) => {
                toast.success(r.message);
                void animateCSS(boton.current, 'tada', { duration: 800 });
                if (siguiente) void navigate(`${base}/${String(clase.id)}/${SLUG[siguiente.key]}`);
              },
              onError: (e) => toast.error(mensajeDe(e)),
            });
          }}
        >
          {clase.progress.caseAnswered ? 'Guardar y continuar' : 'Ver clase'}{' '}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function MasterClass({ tipo, id }: { tipo: Tipo; id: number }) {
  const { data, error, isPending } = usePestana<Video[]>(tipo, id, '/videos');
  const [i, setI] = useState(0);
  if (isPending) return <Cargando alto="aspect-video" />;
  if (error) return <ErrorCarga error={error} />;
  const v = data[i];
  if (!v) return <p className="text-center text-texto-2">Esta clase aún no tiene videos.</p>;
  return (
    <div className="space-y-4">
      <Reproductor embedUrl={v.embedUrl} titulo={v.name} />
      {data.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {data.map((x, k) => (
            <button
              key={x.id}
              type="button"
              className="btn-modulo"
              data-state={k === i ? 'active' : undefined}
              onClick={() => {
                setI(k);
              }}
            >
              {x.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Laminas({
  tipo,
  id,
  sub,
  titulo,
}: {
  tipo: Tipo;
  id: number;
  sub: string;
  titulo: string;
}) {
  const { data, error, isPending, refetch } = usePestana<Lamina[]>(tipo, id, sub, {
    firmadas: true,
  });
  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;
  return (
    <VisorLaminas
      titulo={titulo}
      contenido={['class', id]}
      laminas={data.map((l) => ({ clave: l.id, imageUrl: l.imageUrl, nombre: l.name }))}
      onExpirada={() => refetch()}
    />
  );
}

function Guias({ tipo, id }: { tipo: Tipo; id: number }) {
  const { data, error, isPending, refetch } = usePestana<Guia[]>(tipo, id, '/gpc-guides', {
    firmadas: true,
  });
  const [abierta, setAbierta] = useState<number | null>(null);
  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  const imagenes = data.filter((g) => g.kind === 'image');
  const actual = imagenes.findIndex((g) => g.id === abierta);
  if (actual >= 0) {
    return (
      <div className="space-y-3">
        <button type="button" className="btn-cs btn-contorno" onClick={() => setAbierta(null)}>
          Volver a las guías
        </button>
        <VisorLaminas
          titulo="Guías GPC"
          contenido={['gpc_guide', id]}
          inicial={actual + 1}
          laminas={imagenes.map((g) => ({ clave: g.id, imageUrl: g.fileUrl, nombre: g.name }))}
          onExpirada={() => refetch()}
        />
      </div>
    );
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {data.map((g) => (
        <li key={g.id}>
          <button
            type="button"
            className="card-vidrio card-vidrio-hover flex w-full items-center gap-3 p-4 text-left"
            onClick={() => {
              if (g.kind === 'pdf') window.open(g.fileUrl, '_blank', 'noopener');
              else setAbierta(g.id);
            }}
          >
            <FileText className="shrink-0 text-cian-texto" />
            <span className="font-bold">{g.name}</span>
            <span className="ml-auto text-xs text-texto-3 uppercase">
              {g.kind === 'pdf' ? 'PDF' : 'Lámina'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ResumenesGpc({ tipo, id }: { tipo: Tipo; id: number }) {
  const { data, error, isPending } = usePestana<ResumenDeClase[]>(tipo, id, '/gpc-summaries');
  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {data.map((r) => (
        <li key={r.libraryId}>
          <Link
            to={`/curso-enarm/biblioteca/resumenes-gpc/${String(r.libraryId)}`}
            className="card-vidrio card-vidrio-hover flex items-center gap-3 p-4"
          >
            <FileText className="shrink-0 text-verde-texto" />
            <span className="font-bold">{r.name}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Presentacion({
  tipo,
  id,
  acciones,
}: {
  tipo: Tipo;
  id: number;
  acciones: ReturnType<typeof useAccionClase>;
}) {
  const { data, error, isPending, refetch } = usePestana<PresentacionDeClase>(
    tipo,
    id,
    '/presentation',
    { firmadas: true },
  );
  const espera = useRef<number | undefined>(undefined);
  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;
  return (
    <VisorLaminas
      titulo="Presentación"
      contenido={['class_presentation', data.presentationId]}
      inicial={data.savedSlide}
      laminas={data.slides.map((s) => ({ clave: s.number, imageUrl: s.imageUrl }))}
      onCambio={(n) => {
        window.clearTimeout(espera.current);
        espera.current = window.setTimeout(() => {
          acciones.guardarDiapositiva.mutate(n);
        }, 600);
      }}
      onExpirada={() => refetch()}
    />
  );
}

function Quiz({
  tipo,
  id,
  acciones,
}: {
  tipo: Tipo;
  id: number;
  acciones: ReturnType<typeof useAccionClase>;
}) {
  const { data, error, isPending } = usePestana<ClassQuiz>(tipo, id, '/quiz');
  const [seleccion, setSeleccion] = useState<Letra | null>(null);
  const caja = useRef<HTMLDivElement>(null);
  const justificacion = useRef<HTMLDivElement>(null);
  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  const r = data.result;
  return (
    <div ref={caja} className="panel-vidrio space-y-5">
      <p className="barra-pregunta">{data.question}</p>
      <Opciones
        opciones={data.options}
        seleccion={r?.selected ?? seleccion}
        onSeleccion={setSeleccion}
        deshabilitadas={!!r}
        {...(r
          ? {
              estado: (l: Letra) =>
                l === r.correctOption ? 'correcta' : l === r.selected ? 'incorrecta' : undefined,
            }
          : {})}
      />
      {r ? (
        <div ref={justificacion} className="superficie-lectura space-y-2 p-5">
          <p
            className={cn(
              'flex items-center gap-2 font-extrabold',
              r.correct ? 'text-verde-texto' : 'text-rojo-texto',
            )}
          >
            {r.correct ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
            {r.correct ? '¡Respuesta correcta!' : 'Respuesta incorrecta'}
          </p>
          {!r.correct && (
            <p>
              La respuesta correcta es <strong>{r.correctAnswer}</strong>.
            </p>
          )}
          <p className="leading-relaxed">{r.justification}</p>
        </div>
      ) : (
        <div className="text-right">
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={!seleccion || acciones.responderQuiz.isPending}
            onClick={() => {
              if (!seleccion) return;
              acciones.responderQuiz.mutate(seleccion, {
                onSuccess: (res) => {
                  const op = caja.current?.querySelector<HTMLElement>(
                    `[data-opcion="${seleccion}"]`,
                  );
                  answerFeedback(op ?? null, {
                    correct: !!res?.correct,
                    explanation: justificacion.current,
                  });
                },
                onError: (e) => toast.error(mensajeDe(e)),
              });
            }}
          >
            Responder
          </button>
          <p className="mt-2 text-xs text-texto-3">Tienes un solo intento.</p>
        </div>
      )}
    </div>
  );
}

function Respuesta({
  tipo,
  clase,
  acciones,
}: {
  tipo: Tipo;
  clase: ClassDetail;
  acciones: ReturnType<typeof useAccionClase>;
}) {
  const { data, error, isPending } = usePestana<RespuestaCaso>(tipo, clase.id, '/answer');
  const previa = clase.progress.review;
  const [estrellas, setEstrellas] = useState(previa?.stars ?? 0);
  const [comentario, setComentario] = useState(previa?.comment ?? '');
  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  return (
    <div className="space-y-5">
      {/* «Respuesta al caso clínico» del legacy: mascota con el veredicto + detalle con borde degradado. */}
      <section className="panel-vidrio relative pt-8">
        <h2 className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-[10px] bg-marca px-5 py-1 text-sm font-extrabold whitespace-nowrap text-white uppercase">
          Respuesta al caso clínico
        </h2>
        <div className="superficie-lectura grid gap-6 p-5 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:p-6">
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <img
              src={data.correct ? AVATARES.doctorSenala : AVATARES.doctor}
              alt=""
              className="h-40 w-auto"
            />
            <p className="text-xl font-light">
              Tu respuesta fue:{' '}
              <b className={data.correct ? 'text-verde-texto' : 'text-rojo-texto'}>
                {data.correct ? 'Correcta.' : 'Incorrecta'}
              </b>
              <span className="block font-semibold text-azul-texto">
                {data.correct ? '¡Muy bien, sigue así!' : '¡Esfuérzate más!'}
              </span>
            </p>
          </div>
          <div className="space-y-3 leading-relaxed md:border-l-[3px] md:pl-6 md:[border-image:var(--gradiente-marca)_1]">
            <TituloCaso>Caso clínico</TituloCaso>
            <p className="font-bold">{data.case}</p>
            <p>
              <b>PREGUNTA: </b>
              {data.question}
            </p>
            <p className="flex items-start gap-2">
              {data.correct ? (
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-verde-texto" />
              ) : (
                <XCircle size={20} className="mt-0.5 shrink-0 text-rojo-texto" />
              )}
              <span>
                <b className={data.correct ? 'text-verde-texto' : 'text-rojo-texto'}>
                  TU RESPUESTA:{' '}
                </b>
                {data.yourAnswer}
              </span>
            </p>
            {!data.correct && (
              <p>
                <b className="text-verde-texto">RESPUESTA CORRECTA: </b>
                {data.correctAnswer}
              </p>
            )}
            <div className="pt-3">
              <TituloCaso oscuro>Justificación</TituloCaso>
              <p className="mt-3">
                <TextoMarcado texto={data.justification} />
              </p>
            </div>
          </div>
        </div>
      </section>
      <form
        className="panel-vidrio space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (estrellas < 1) return;
          acciones.resena.mutate(
            { stars: estrellas, comment: comentario },
            {
              onSuccess: () => toast.success('¡Gracias! Guardamos tu opinión de la clase.'),
              onError: (er) => toast.error(mensajeDe(er)),
            },
          );
        }}
      >
        <h3 className="font-extrabold">¿Qué te pareció esta clase?</h3>
        <p className="text-sm text-texto-2">
          Para <b className="text-verde-texto">CONSALUD</b> tu opinión es lo más importante. Si
          deseas, escríbenos un comentario para seguir mejorando para ti.
        </p>
        <div role="radiogroup" aria-label="Calificación" className="flex gap-1">
          {[0, 1, 2, 3, 4].map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={estrellas === k + 1}
              aria-label={`${String(k + 1)} de 5`}
              onClick={() => {
                setEstrellas(k + 1);
              }}
            >
              <Star
                size={26}
                className={k < estrellas ? 'fill-amarillo-400 text-amarillo-400' : 'text-texto-3'}
              />
            </button>
          ))}
        </div>
        <textarea
          className="input-vidrio min-h-20 w-full"
          maxLength={2000}
          placeholder="Comentario (opcional)"
          value={comentario}
          onChange={(e) => {
            setComentario(e.target.value);
          }}
        />
        <button
          type="submit"
          className="btn-cs btn-primario"
          disabled={estrellas < 1 || acciones.resena.isPending}
        >
          {previa ? 'Actualizar opinión' : 'Enviar opinión'}
        </button>
      </form>
    </div>
  );
}

/** Rótulo con línea del legacy («CASO CLÍNICO» claro, «JUSTIFICACIÓN» marino). */
function TituloCaso({ oscuro, children }: { oscuro?: boolean; children: string }) {
  const color = oscuro ? 'bg-marino-800' : 'bg-azul-400';
  return (
    <div className="flex items-center gap-0">
      <span
        className={cn(
          'shrink-0 rounded-[10px] px-4 py-0.5 text-sm font-bold text-white uppercase',
          color,
        )}
      >
        {children}
      </span>
      <span aria-hidden className={cn('h-[3px] flex-1', color)} />
    </div>
  );
}
